import { db, createDrawNotification } from '../config/firebase';
import { collection, getDoc, addDoc, doc, getDocs, deleteDoc } from 'firebase/firestore';

interface Winner {
  userId: string;
  amount: number;
}

export const performRandomDraw = async (numberOfWinners: number, totalAmount: number): Promise<Winner[]> => {
  try {
    const participantsRef = doc(db, 'collectiveInvestment', 'current');
    const snap = await getDoc(participantsRef);
    const participants = snap.data()?.participants || [];
    
    if (!participants || participants.length === 0) {
      throw new Error('Aucun participant trouvé');
    }

    // Mélanger les participants
    const shuffled = [...participants].sort(() => Math.random() - 0.5);
    
    // Prendre uniquement le nombre demandé de gagnants uniques
    const uniqueWinners = shuffled.slice(0, numberOfWinners);
    
    // S'assurer que chaque gagnant a un userId valide
    return uniqueWinners.map(participant => {
      if (typeof participant !== 'string') {
        throw new Error('Format de participant invalide');
      }
      
      return {
        userId: participant,
        amount: Math.floor(totalAmount / numberOfWinners)
      };
    });
  } catch (error) {
    console.error('Erreur lors du tirage:', error);
    throw error;
  }
};

export const addDrawResult = async (drawResult: any) => {
  try {
    const drawHistoryRef = collection(db, 'drawHistory');
    
    // Vérification plus stricte des données
    if (!drawResult?.winners || !Array.isArray(drawResult.winners)) {
      throw new Error('Les données des gagnants sont invalides');
    }

    // Transformer et valider chaque gagnant
    const validatedWinners = drawResult.winners.map((winner: any) => {
      if (!winner || typeof winner !== 'object') {
        throw new Error('Format de gagnant invalide');
      }

      const userId = winner.userId;
      const amount = Number(winner.amount);

      if (!userId || typeof userId !== 'string') {
        console.error('Winner data:', winner); // Pour le débogage
        throw new Error('UserId invalide ou manquant');
      }

      if (isNaN(amount) || amount <= 0) {
        throw new Error('Montant invalide');
      }

      return {
        userId,
        amount,
        claimed: false,
        claimedAt: null,
        createdAt: new Date()
      };
    });

    const drawData = {
      winners: validatedWinners,
      totalAmount: Number(drawResult.totalAmount) || 0,
      createdAt: new Date(),
      participantsCount: Number(drawResult.participantsCount) || 0,
      status: 'completed'
    };

    // Vérification finale
    if (drawData.winners.length === 0) {
      throw new Error('Aucun gagnant valide');
    }

    const docRef = await addDoc(drawHistoryRef, drawData);
    
    // Créer une notification pour chaque gagnant
    const notificationPromises = drawData.winners.map((winner: Winner) => 
      createDrawNotification(winner.userId, {
        drawId: docRef.id,
        amount: winner.amount
      })
    );
    
    await Promise.all(notificationPromises);
    
    return docRef.id;
  } catch (error) {
    console.error("Erreur lors de l'ajout du résultat:", error);
    throw error;
  }
};

export const formatCountdown = (timeLeft: number): string => {
  const hours = Math.floor(timeLeft / (1000 * 60 * 60));
  const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);
  
  return `${hours}h ${minutes}m ${seconds}s`;
};

export const clearDrawHistory = async () => {
  try {
    const drawsRef = collection(db, 'draws');
    const snapshot = await getDocs(drawsRef);
    
    const deletePromises = snapshot.docs.map(doc => 
      deleteDoc(doc.ref)
    );
    
    await Promise.all(deletePromises);
    console.log('Historique des tirages effacé avec succès');
    return true;
  } catch (error) {
    console.error('Erreur lors de la suppression de l\'historique:', error);
    return false;
  }
}; 